import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { LogOut, User, LogIn } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { getUserRoles, getRoleDisplayName } from '../utils/roleUtils';
import { getSessionFromLocalStorage, clearSessionFromLocalStorage } from '../utils/sessionManager';
import { getStoredSessionToken, clearSessionToken } from '../utils/apiHeaders';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { supabase } from '../utils/supabase/client';

interface AuthButtonProps {
  variant?: 'default' | 'outline' | 'ghost';
  className?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  company: any | null;
}

export function AuthButton({ variant = 'outline', className = '' }: AuthButtonProps) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    company: null,
  });

  // Load auth state on mount
  useEffect(() => {
    const loadAuthState = () => {
      const { sessionId, user, company } = getSessionFromLocalStorage();
      
      if (sessionId && user) {
        setAuthState({
          isAuthenticated: true,
          user,
          company,
        });
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          company: null,
        });
      }
    };

    loadAuthState();

    // Listen for storage events (cross-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.includes('pathkit_')) {
        loadAuthState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check session periodically
    const interval = setInterval(loadAuthState, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = async () => {
    console.log('[AuthButton] Logging out');
    
    try {
      // ENTERPRISE AUTH: Call backend session destroy with bearer token
      const sessionToken = getStoredSessionToken();
      
      if (sessionToken) {
        // Destroy session on backend
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/session/destroy`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'apikey': publicAnonKey,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
          }
        );
        
        if (!response.ok) {
          console.warn('[AuthButton] Session destroy returned non-200, continuing with local cleanup');
        } else {
          console.log('[AuthButton] Session destroyed on backend');
        }
      }
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // ENTERPRISE AUTH: Clear canonical session token
      clearSessionToken();
      
    } catch (error) {
      console.error('[AuthButton] Error during logout:', error);
    }
    
    // Clear local storage (legacy cleanup)
    clearSessionFromLocalStorage();
    
    // Redirect to login or home
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    
    if (subdomain && subdomain !== 'pathkit' && subdomain !== 'www' && !hostname.includes('localhost')) {
      // On tenant subdomain - reload to show login
      window.location.href = '/';
    } else {
      // On main domain or localhost - redirect to home
      window.location.href = '/';
    }
  };

  const handleLogin = () => {
    console.log('[AuthButton] Navigating to login');
    
    // Check if we're on a tenant subdomain
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    
    if (subdomain && subdomain !== 'pathkit' && subdomain !== 'www' && !hostname.includes('localhost')) {
      // On tenant subdomain - redirect to login
      window.location.href = '/';
    } else {
      // On main domain or localhost - redirect to login page
      window.location.href = '/login';
    }
  };

  if (authState.isAuthenticated && authState.user) {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant={variant} 
            className={`${className}`}
            id="auth-button-trigger"
          >
            <User className="h-4 w-4 mr-2" />
            {authState.user.name || authState.user.email}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-56 bg-white border border-gray-200 shadow-lg"
          sideOffset={5}
        >
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>
            <User className="h-4 w-4 mr-2" />
            {authState.user.email}
          </DropdownMenuItem>
          {authState.user.role && (
            <DropdownMenuItem disabled>
              Role: {getRoleDisplayName(authState.user.role)}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Log Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button variant={variant} onClick={handleLogin} className={className}>
      <LogIn className="h-4 w-4 mr-2" />
      Sign In
    </Button>
  );
}